/**
 * One company's brain, at an address somebody can be sent to.
 *
 * The card itself is unchanged - the SAME `BrainCard` the register detail page
 * stacks and `BrainDialog` puts in a modal. Three mountings, one component: a
 * second copy would drift the day somebody adds a tab to one of them.
 *
 * THE TABS ARE A PINNED MENU HERE AND NOWHERE ELSE (`nav="rail"`). This page is
 * the only mounting that is nothing but the card: it has the full content width
 * to give a 214px column, and its panels are long enough that a strip above them
 * scrolls out of reach. The dialog and the stacked register card keep the pills.
 *
 * THE DIALOG DOES NOT GO AWAY. The leads and follow-up tabs keep opening
 * the card over themselves, because their filters and scroll position are
 * component state and navigating away throws away a list somebody just built.
 * This page is for the other case: arriving from outside with nothing to lose -
 * a link in the bell, a link in a message.
 *
 * THE DOMAIN GUARD IS NOT DECORATION. `useBrain` has no empty-key check: it
 * would fire `GET /admin/ariregister/brain/` with no domain, and the 404 is
 * swallowed as "never run", so a broken URL would render a confident invitation
 * to audit nothing.
 *
 * THE COMPANY IS A HEADING, NOT A PAGE TITLE. This page sits under the
 * Arendusaju tab of Kliendiotsing, whose head owns the one `h1`. The domain and
 * the way back to the list share a toolbar row over the card instead, the same
 * row every other tab keeps its own controls in.
 */
import { ArrowLeft, Brain } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { useT } from "../../../i18n/I18nProvider";
import { userCanPlatform, type StaffUser } from "../../../lib/auth";
import { brainKey } from "../../../lib/siteBrain";
import { EmptyState, Toolbar } from "../../ui";
import { PopHost } from "../../shell/Pop";
import { BrainCard } from "../ariregister/brain";
import { stepPath } from "../prospecting/steps";

export function BrainDetailPage({ user }: { user: StaffUser }) {
  const t = useT();
  const { domain = "" } = useParams();
  const canManage = userCanPlatform(user, "manage:ariregister");

  // Normalised with the SAME function the client uses for every other brain
  // request. A key built any other way answers 422 invalid_url rather than 404,
  // which reads as a broken page instead of a mistyped address.
  const key = brainKey(decodeURIComponent(domain));

  return (
    <PopHost>
      <Toolbar>
        {/* A link, not a button: it goes somewhere, and middle-click opens the
            list in a tab of its own. */}
        <Link to={stepPath("brain")} className="crm-cb crm-cb-wide crm-cb-sm">
          <ArrowLeft className="size-3.5" />
          {t("brain.detail.back")}
        </Link>
        <h2 className="crm-card-t crm-trunc min-w-0">{key || t("brain.detail.noDomain")}</h2>
      </Toolbar>

      {key ? (
        <BrainCard domain={key} canManage={canManage} me={user.id} t={t} nav="rail" />
      ) : (
        <div className="crm-card">
          <EmptyState icon={Brain} title={t("brain.detail.noDomain")} body={t("brain.detail.noDomainBody")} />
        </div>
      )}
    </PopHost>
  );
}
